<template>
  <form
    @submit.prevent="submit"
    class="standard-product-family-form uk-form-stacked"
  >
    <!-- Basic Data -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.basicFields')}}</legend>

      <readonly-form-field
        :errors="fields.errors.name"
        :label="$t('fields.name')"
      >
        <text-placeholder :text="name" />
      </readonly-form-field>

      <brand-combobox
        :disabled="!isEditable"
        :errors="fields.errors.brand"
        :label="$t('fields.brand')"
        v-model="fields.values.brand"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.model"
        :label="$t('fields.model')"
        ref="modelInput"
        type="text"
        v-model="fields.values.model"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.familyNumber"
        :label="$t('fields.familyNumber')"
        type="text"
        v-model="fields.values.familyNumber"
      />

      <checkbox-group-input
        :collection="availableSelectors"
        :disabled="!isEditable"
        :errors="fields.errors.selectors"
        :label="$t('fields.selectors')"
        type="text"
        v-model="fields.values.selectors"
      />

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.popularity"
        :label="$t('fields.popularity')"
        type="number"
        v-model.number="fields.values.popularity"
      />

      <radio-group-input
        :disabled="!isEditable"
        :errors="fields.errors.origin"
        :collection="availableOrigins"
        :inline="false"
        :label="$t('fields.origin')"
        v-model="fields.values.origin"
      />
    </fieldset>

    <!-- Categories -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.categories')}}</legend>

      <product-category-assignment-manager
        :disabled="!isEditable"
        :errors="fields.errors.categories"
        :value="fields.values.categories"
        @change="fields.change.categories"
      />
    </fieldset>

    <!-- Details -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.details')}}</legend>

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.altName"
        :label="$t('fields.altName')"
        type="text"
        v-model="fields.values.altName"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.usps"
        :label="$t('fields.usps')"
        v-model="fields.values.usps"
      />

      <text-list-input
        :disabled="!isEditable"
        :errors="fields.errors.summaryTexts"
        :label="$t('fields.summaryTexts')"
        v-model="fields.values.summaryTexts"
      />

      <dimensions-input
        :disabled="!isEditable"
        :errors="fields.errors.dimensions"
        :label="$t('fields.dimensions')"
        v-model="fields.values.dimensions"
      />

      <weight-input
        :disabled="!isEditable"
        :errors="fields.errors.weight"
        :label="$t('fields.weight')"
        v-model="fields.values.weight"
      />
    </fieldset>

    <!-- Manufacturer Contacts -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{ $t('fieldsets.manufacturerContact') }}</legend>
      <textarea-input
        :disabled="!isEditable"
        :errors="fields.errors.manufacturerContact"
        :label="$t('fields.manufacturerContact')"
        rows="5"
        v-model="fields.values.manufacturerContact"
      />
      <textarea-input
        :disabled="!isEditable"
        :errors="fields.errors.manufacturerContactEu"
        :label="$t('fields.manufacturerContactEu')"
        rows="5"
        v-model="fields.values.manufacturerContactEu"
      />
    </fieldset>

    <!-- SEO -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.seo')}}</legend>

      <text-input
        :disabled="!isEditable"
        :errors="fields.errors.slug"
        :label="$t('fields.slug')"
        :placeholder="slugPlaceholder"
        type="text"
        v-model="fields.values.slug"
      />

      <textarea-input
        :disabled="!isEditable"
        :errors="fields.errors.description"
        :label="$t('fields.description')"
        rows="5"
        v-model="fields.values.description"
      />
    </fieldset>

    <!-- Energy Efficiency -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend">{{$t('fieldsets.energy')}}</legend>

      <energy-efficiency-select
        :disabled="!isEditable"
        :errors="fields.errors.energyEfficiencyClass"
        :label="$t('fields.energyEfficiencyClass')"
        v-model="fields.values.energyEfficiencyClass"
      />

      <attachment-input
        :disabled="!isEditable"
        :errors="fields.errors.energyLabelImage"
        :label="$t('fields.energyLabelImage')"
        attachment-type="IMAGE"
        v-model="fields.values.energyLabelImage"
      />

      <attachment-input
        :disabled="!isEditable"
        :errors="fields.errors.energyDocument"
        :label="$t('fields.energyDocument')"
        attachment-type="DOCUMENT"
        v-model="fields.values.energyDocument"
      />
    </fieldset>

    <!-- Legal Texts -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.legalTexts')}}</legend>

      <legal-text-manager
        :disabled="!isEditable"
        :errors="fields.errors.legalTexts"
        v-model="fields.values.legalTexts"
      />
    </fieldset>

    <!-- Documents -->
    <fieldset class="uk-fieldset uk-margin-medium-top">
      <legend class="uk-legend uk-margin">{{$t('fieldsets.documents')}}</legend>

      <attachment-manager
        :disabled="!isEditable"
        :errors="fields.errors.documents"
        attachment-type="DOCUMENT"
        v-model="fields.values.documents"
      />
    </fieldset>

    <div class="uk-margin-medium-top">
      <submit-button :disabled="!isSubmittable" type="primary" />
      <cancel-button />
    </div>
  </form>
</template>

<script>
import camelCase from 'lodash/camelCase';
import Authentication from '@/mixins/Authentication';
import AttachmentInput from '@/components/AttachmentInput';
import AttachmentManager from '@/components/AttachmentManager';
import BrandCombobox from '@/components/BrandCombobox';
import CancelButton from '@/components/CancelButton';
import CheckboxGroupInput from '@/components/CheckboxGroupInput';
import CommonButton from '@/components/CommonButton';
import DimensionsInput from '@/components/DimensionsInput';
import EnergyEfficiencySelect from '@/components/EnergyEfficiencySelect';

import Form from '@/mixins/Form';
import LegalTextManager from '@/components/LegalTextManager';
import ProductCategoryAssignmentManager
  from '@/components/ProductCategoryAssignmentManager';
import RadioGroupInput from '@/components/RadioGroupInput';
import ReadonlyFormField from '@/components/ReadonlyFormField';
import slugQuery from '@/api/queries/slug.graphql';
import SubmitButton from '@/components/SubmitButton';
import TextareaInput from '@/components/TextareaInput';
import TextInput from '@/components/TextInput';
import TextListInput from '@/components/TextListInput';
import TextPlaceholder from '@/components/TextPlaceholder';
import WeightInput from '@/components/WeightInput';

const AVAILABLE_SELECTORS = ['COLOR', 'SIZE', 'MEMORY', 'STORAGE'];

export default {
  name: 'standard-product-family-form',
  mixins: [
    Authentication,
    Form({
      altName: true,
      brand: { required: true, type: Form.Types.Reference },
      categories: { type: Form.Types.ReferenceList },
      documents: { type: Form.Types.ReferenceList },
      description: true,
      dimensions: { type: Form.Types.NullableObject },
      energyEfficiencyClass: true,
      energyLabelImage: { type: Form.Types.Reference },
      energyDocument: { type: Form.Types.Reference },
      familyNumber: true,
      legalTexts: { type: Form.Types.ReferenceList },
      manufacturerContact: true,
      manufacturerContactEu: true,
      model: { required: true },
      name: { dumpTo: null },
      origin: true,
      popularity: { default: 0 },
      selectors: { type: Form.Types.Array },
      slug: true,
      summaryTexts: { type: Form.Types.Array },
      usps: { type: Form.Types.Array },
      weight: { type: Form.Types.NullableObject },
    }),
  ],
  components: {
    AttachmentInput,
    AttachmentManager,
    BrandCombobox,
    CancelButton,
    CheckboxGroupInput,
    CommonButton,
    DimensionsInput,
    EnergyEfficiencySelect,
    LegalTextManager,
    ProductCategoryAssignmentManager,
    RadioGroupInput,
    ReadonlyFormField,
    SubmitButton,
    TextareaInput,
    TextInput,
    TextListInput,
    TextPlaceholder,
    WeightInput,
  },
  mounted() {
    this.$refs.modelInput.focus();
  },
  apollo: {
    slugPlaceholder: {
      query: slugQuery,
      variables() {
        return { value: this.name };
      },
      fetchPolicy: 'network-only',
      debounce: { wait: 500 },
      update({ slug }) {
        return slug;
      },
      skip() {
        return !this.name;
      },
    },
  },
  computed: {
    id() {
      return this.$route.params.id;
    },
    availableSelectors() {
      return AVAILABLE_SELECTORS.map(selector => (
        {
          label: this.$t(`fields.${camelCase(selector)}`),
          value: selector,
        }
      ));
    },
    availableOrigins() {
      return [
        { label: 'Brodos', value: 'brodos' },
        { label: 'PXSDB', value: 'pxsdb' },
        { label: 'Manuell gepflegt', value: 'manual' },
      ];
    },
    brandName() {
      const brand = this.fields.values.brand;
      return brand && brand.name;
    },
    nameWithDefault() {
      return this.name || '—';
    },
    name() {
      const model = this.fields.values.model;
      if (this.brandName && model) {
        return `${this.brandName} ${model}`.trim();
      }
      return null;
    },
  },
};
</script>



// WEBPACK FOOTER //
// StandardProductFamilyForm.vue?0f47e4fa